import React, { FC, useCallback, useMemo } from 'react';
import dayjs from 'dayjs';
import { useTranslations } from '@altermaind/core/i18n';
import { Card } from '@altermaind/web/ui';
import { useDialog } from '@/_shared/hooks/useDialog';
import { DialogStartDocumentPayload } from '@/training/documents/components/DialogStartDocument/types';
import { getContentDetailsCall } from '@/training/hardSkills/state';
import { CardsContent } from '../CardsContent';
import { CardsHeader } from '../CardsHeader';

type BaseProps = {
  id: string;
  thumbnailUrl?: string;
  durationInMinutes?: number;
  category?: string;
  title?: string;
  description: string;
  inProgress?: boolean;
  rating?: number;
  isFavorite?: boolean;
  isCompleted?: boolean;
  isSkillToBoost?: boolean;
};

type Props<Loading extends boolean = boolean> = Loading extends true
  ? Partial<BaseProps> & { loading: true }
  : BaseProps & { loading?: false };

export const Document: FC<Props> = ({
  id,
  thumbnailUrl,
  rating,
  durationInMinutes,
  category,
  title,
  description,
  loading = false,
  inProgress = false,
  isFavorite = false,
  isCompleted = false,
  isSkillToBoost = false,
}) => {
  const t = useTranslations('trainingSoftSkills.cards.DOCUMENT');
  const { loading: loadingGetContentDetails } = getContentDetailsCall.hook();
  const { openDialog: openDialogDocumentStarter } =
    useDialog<DialogStartDocumentPayload>('documentStarter');

  const onDocumentStart = useCallback(() => {
    if (id) {
      openDialogDocumentStarter({
        documentId: id,
        isSkillToBoost,
      });
    }
  }, [id, isSkillToBoost, openDialogDocumentStarter]);

  const textButton = useMemo(() => {
    if (inProgress) {
      return t('resume');
    }
    if (isCompleted) {
      return t('restart');
    }
    return t('start');
  }, [inProgress, isCompleted, t]);

  const iconButton = useMemo(() => {
    if (inProgress) {
      return 'eyeOn';
    }
    if (isCompleted) {
      return 'refresh';
    }
    return 'eyeOn';
  }, [inProgress, isCompleted]);

  return (
    <Card
      width='100%'
      padding='none'
      justifyContent='space-between'
      display='flex'
      flexDirection='column'
      height='100%'
    >
      <CardsHeader
        contentId={id as string}
        mainInfoContent={
          durationInMinutes
            ? {
                text: t('duration', {
                  time: dayjs.duration(durationInMinutes ?? 0, 'minutes').asMinutes(),
                }),
                iconName: 'clock',
              }
            : undefined
        }
        thumbnailUrl={thumbnailUrl}
        loading={loading}
        rating={rating}
        isFavorite={isFavorite}
        inProgress={inProgress}
        isCompleted={isCompleted}
        isSkillToBoost={isSkillToBoost}
        category={category}
        title={title}
      />
      <CardsContent
        button={{
          iconName: iconButton,
          text: textButton,
          onClick: onDocumentStart,
          loading: loadingGetContentDetails,
        }}
        description={description || ''}
        loading={loading}
      />
    </Card>
  );
};
